import React from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { useUpdateNodeConfig, useUpdateNodeVisibility } from '../../hooks/useNode';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { 
  Type, 
  Settings2, 
  MessageSquareOff, 
  Share2, 
  Info,
  Layers,
  Globe,
  ShieldCheck,
  Lock
} from 'lucide-react';
import { cn } from '../../lib/utils';
import toast from 'react-hot-toast';

export const TerminologyPage = () => {
  const { node } = useTenant();
  const updateConfigMutation = useUpdateNodeConfig(node?.id || '');
  const updateVisibilityMutation = useUpdateNodeVisibility(node?.id || '');
  
  const [terms, setTerms] = React.useState(node?.config?.terminology || {});
  const [visibility, setVisibility] = React.useState(node?.visibility || 'PUBLIC');
  const [features, setFeatures] = React.useState(node?.config?.features || {
    allowDirectUpstreamMessages: true,
    enforceParentTerminology: false
  });

  // Sync local governance fields when the node changes (render-phase adjustment).
  const [prevTermsNode, setPrevTermsNode] = React.useState<typeof node | null>(null);
  if (node && prevTermsNode !== node) {
    setPrevTermsNode(node);
    if (node.config?.terminology) setTerms(node.config.terminology);
    if (node.visibility) setVisibility(node.visibility);
    if (node.config?.features) setFeatures(node.config.features);
  }

  const handleSave = async () => {
    try {
      await Promise.all([
        updateConfigMutation.mutateAsync({
          terminology: terms,
          features: features
        }),
        updateVisibilityMutation.mutateAsync(visibility)
      ]);
      toast.success('Governance settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const updateTerm = (key: string, val: string) => {
    setTerms(prev => ({ ...prev, [key]: val }));
  };

  const toggleFeature = (key: string) => {
    setFeatures(prev => ({ ...prev, [key]: !prev[key as keyof typeof prev] }));
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex items-center gap-4 mb-8">
        <Button variant="secondary" size="sm" onClick={() => window.history.back()}>
          Back
        </Button>
        <div>
          <h1 className="text-lg md:text-2xl font-semibold text-gray-900 dark:text-white">Governance & Terminology</h1>
          <p className="text-xs md:text-sm text-gray-500">Define how your organization is named and how it interacts with the hierarchy.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2 space-y-6">
          {/* Terminology Section */}
          <Card className="space-y-6">
            <div className="flex items-center gap-2 mb-4">
              <Type size={18} className="text-brand-primary" />
              <h3 className="font-bold text-navy-900">Module Terminology</h3>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Input 
                label="Ledger (Financials)"
                placeholder="e.g. Treasury"
                value={terms.ledger || ''}
                onChange={(e) => updateTerm('ledger', e.target.value)}
              />
              <Input 
                label="Requirements (Tasks)"
                placeholder="e.g. Compliance"
                value={terms.requirements || ''}
                onChange={(e) => updateTerm('requirements', e.target.value)}
              />
              <Input 
                label="Goals (Objectives)"
                placeholder="e.g. Key Results"
                value={terms.goals || ''}
                onChange={(e) => updateTerm('goals', e.target.value)}
              />
              <Input 
                label="Pulse (Feed)"
                placeholder="e.g. Gazette"
                value={terms.pulse || ''}
                onChange={(e) => updateTerm('pulse', e.target.value)}
              />
              <Input 
                label="Message Board"
                placeholder="e.g. Forum"
                value={terms.discussions || ''}
                onChange={(e) => updateTerm('discussions', e.target.value)}
              />
              <Input 
                label="Member (Singular)"
                placeholder="e.g. Soldier"
                value={terms.member || ''}
                onChange={(e) => updateTerm('member', e.target.value)}
              />
              <Input 
                label="Support (Help Desk)"
                placeholder="e.g. Incident Center"
                value={terms.support || ''}
                onChange={(e) => updateTerm('support', e.target.value)}
              />
              <Input 
                label="Parent Org (HQ)"
                placeholder="e.g. Command"
                value={terms.parentNode || ''}
                onChange={(e) => updateTerm('parentNode', e.target.value)}
              />
              <Input 
                label="Child Org (Sub-group)"
                placeholder="e.g. Unit"
                value={terms.childNode || ''}
                onChange={(e) => updateTerm('childNode', e.target.value)}
              />
            </div>

            <div className="p-4 bg-blue-50 rounded-none border-2 border-black dark:border-white border-blue-100 flex gap-3 text-blue-700">
               <Info size={18} className="flex-shrink-0" />
               <p className="text-xs leading-relaxed">
                 These terms will be used throughout your organization's UI, including the sidebar and page headers.
               </p>
            </div>
          </Card>

          {/* Visibility Section */}
          <Card className="space-y-6">
            <div className="flex items-center gap-2 mb-4">
              <Globe size={18} className="text-blue-600" />
              <h3 className="font-bold text-navy-900">Org Visibility</h3>
            </div>

            <div className="grid grid-cols-1 gap-3">
              {[
                { 
                  id: 'PUBLIC', 
                  icon: Globe, 
                  label: 'Public Org', 
                  desc: 'Visible to everyone. Anyone can join instantly as a guest.',
                  color: 'text-blue-500',
                  bgColor: 'bg-blue-50'
                },
                { 
                  id: 'PROTECTED', 
                  icon: ShieldCheck, 
                  label: 'Protected Org', 
                  desc: 'Searchable, but users must request an invite to join.',
                  color: 'text-amber-500',
                  bgColor: 'bg-amber-50'
                },
                { 
                  id: 'PRIVATE', 
                  icon: Lock, 
                  label: 'Private Org', 
                  desc: 'Hidden from search. New members only via direct invite.',
                  color: 'text-rose-500',
                  bgColor: 'bg-rose-50'
                }
              ].map((option) => (
                <button
                  key={option.id}
                  onClick={() => setVisibility(option.id as any)}
                  className={cn(
                    "flex flex-col sm:flex-row sm:items-center gap-4 p-4 rounded-none border-2 border-black dark:border-white transition-all text-left group",
                    visibility === option.id 
                      ? "border-brand-primary bg-brand-primary/5 ring-1 ring-brand-primary/20" 
                      : "border-black dark:border-white hover:border-black dark:border-white"
                  )}
                >
                  <div className={cn("w-12 h-12 rounded-none flex items-center justify-center shrink-0", option.bgColor, option.color)}>
                    <option.icon size={24} />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-gray-900 flex items-center gap-2 dark:text-white">
                       {option.label}
                       {visibility === option.id && <div className="w-1.5 h-1.5 rounded-none bg-brand-primary" />}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">{option.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </Card>

          {/* Privacy & Communication Section */}
          <Card className="space-y-6">
            <div className="flex items-center gap-2 mb-4">
              <Settings2 size={18} className="text-teal-600" />
              <h3 className="font-bold text-navy-900">Communication & Hierarchy</h3>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 border-2 border-black dark:border-white rounded-none hover:bg-white transition-colors cursor-pointer dark:bg-slate-950" onClick={() => toggleFeature('allowDirectUpstreamMessages')}>
                <div className={cn(
                  "mt-1 w-10 h-5 rounded-none relative transition-colors",
                  features.allowDirectUpstreamMessages ? "bg-teal-500" : "bg-gray-200"
                )}>
                  <div className={cn(
                    "absolute top-1 left-1 w-3 h-3 bg-white rounded-none transition-transform dark:bg-slate-900",
                    features.allowDirectUpstreamMessages ? "translate-x-5" : "translate-x-0"
                  )} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900 italic dark:text-white">Allow direct upstream messaging</p>
                  <p className="text-xs text-gray-500 mt-1">If disabled, members can only post to the parent organization's public channels, not individual parent members.</p>
                </div>
                <MessageSquareOff size={18} className="text-gray-300" />
              </div>

              <div className="flex items-start gap-4 p-4 border-2 border-black dark:border-white rounded-none hover:bg-white transition-colors cursor-pointer dark:bg-slate-950" onClick={() => toggleFeature('enforceParentTerminology')}>
                <div className={cn(
                  "mt-1 w-10 h-5 rounded-none relative transition-colors",
                  features.enforceParentTerminology ? "bg-brand-primary" : "bg-gray-200"
                )}>
                  <div className={cn(
                    "absolute top-1 left-1 w-3 h-3 bg-white rounded-none transition-transform dark:bg-slate-900",
                    features.enforceParentTerminology ? "translate-x-5" : "translate-x-0"
                  )} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900 italic dark:text-white">Enforce parent terminology</p>
                  <p className="text-xs text-gray-500 mt-1">Automatically inherit custom module naming from your parent organization (e.g. if HQ calls Ledger "Treasury", so do you).</p>
                </div>
                <Share2 size={18} className="text-gray-300" />
              </div>
            </div>
          </Card>

          <div className="flex justify-end gap-3">
             <Button variant="ghost" onClick={() => window.history.back()}>Discard</Button>
             <Button 
              onClick={handleSave} 
              isLoading={updateConfigMutation.isPending || updateVisibilityMutation.isPending} 
              className="px-10"
             >
               Save Governance
             </Button>
          </div>
        </div>

        <div className="space-y-6">
           <Card className="bg-white border-dashed dark:bg-slate-950">
              <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono mb-4">Sidebar Preview</h4>
              <div className="space-y-2 pb-4">
                 <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] dark:bg-slate-900">
                    <Layers size={14} className="text-gray-400" />
                    <span className="text-sm font-medium text-gray-700">{terms.ledger || 'Ledger'}</span>
                 </div>
                 <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] opacity-50 dark:bg-slate-900">
                    <Layers size={14} className="text-gray-400" />
                    <span className="text-sm font-medium text-gray-700">{terms.goals || 'Goals'}</span>
                 </div>
              </div>
           </Card>
           
           <div className="p-6 bg-navy-900 rounded-custom text-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] relative overflow-hidden">
              <h4 className="font-bold mb-2">Pro Tip</h4>
              <p className="text-xs text-blue-200 leading-relaxed relative z-10">
                Custom terminology helps clarify your organization's mission. For example, a sports team might call their Ledger "Team Dues" and their Members "Athletes".
              </p>
              <div className="absolute -bottom-4 -right-4 w-20 h-20 bg-brand-primary/20 rounded-none blur-2xl" />
           </div>
        </div>
      </div>
    </div>
  );
};
